import React from "react";
import "./notification-icon.scss";

export type NotificationIconSize = "small" | "medium" | "large";
export type NotificationIndicatorType = "dot" | "count" | "pulse";

export interface NotificationIconProps {
  /** Icon rendered in the center (e.g. react-feather Bell, custom SVG). */
  icon: React.ReactNode;
  /** Unread count; when greater than 0 the indicator is shown unless overridden by `showIndicator`. */
  count?: number;
  /** Explicitly show or hide the indicator. When omitted, shown when `count` > 0. */
  showIndicator?: boolean;
  /** Indicator style: static dot, numeric badge, or pulsing dot. */
  indicatorType?: NotificationIndicatorType;
  size?: NotificationIconSize;
  /** Icon dimensions in px; overrides the `size` preset. */
  iconSize?: number;
  /** Indicator dot or badge size in px; overrides the `size` preset for dot/pulse. */
  indicatorSize?: number;
  /** Cap displayed count (e.g. 99 → "99+"). Defaults to 99. */
  maxCount?: number;
  indicatorBackgroundColor?: string;
  indicatorTextColor?: string;
  /** Fine-tune indicator position relative to the icon corner. */
  indicatorOffset?: { top?: string; right?: string };
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Accessible label; defaults to a count-aware notifications label. */
  ariaLabel?: string;
  disabled?: boolean;
}

const formatCount = (count: number, maxCount: number): string => {
  if (count > maxCount) return `${maxCount}+`;
  return String(count);
};

const shouldShowIndicator = (
  showIndicator: boolean | undefined,
  count: number
): boolean => {
  if (showIndicator === false) return false;
  if (showIndicator === true) return true;
  return count > 0;
};

export const NotificationIcon: React.FC<NotificationIconProps> = ({
  icon,
  count = 0,
  showIndicator,
  indicatorType = "dot",
  size = "medium",
  iconSize,
  indicatorSize,
  maxCount = 99,
  indicatorBackgroundColor,
  indicatorTextColor,
  indicatorOffset,
  className = "",
  onClick,
  ariaLabel,
  disabled = false,
}) => {
  const resolvedType = indicatorType;
  const visible = shouldShowIndicator(showIndicator, count);
  const displayCount = formatCount(count, maxCount);

  const rootStyle = {
    ...(iconSize != null
      ? { "--notification-icon-size": `${iconSize}px` }
      : {}),
    ...(indicatorSize != null
      ? { "--notification-indicator-size": `${indicatorSize}px` }
      : {}),
    ...(indicatorOffset?.top != null
      ? { "--notification-indicator-top": indicatorOffset.top }
      : {}),
    ...(indicatorOffset?.right != null
      ? { "--notification-indicator-right": indicatorOffset.right }
      : {}),
  } as React.CSSProperties;

  const indicatorStyle: React.CSSProperties = {
    ...(indicatorBackgroundColor
      ? { backgroundColor: indicatorBackgroundColor }
      : {}),
    ...(indicatorTextColor ? { color: indicatorTextColor } : {}),
  };

  const defaultAriaLabel =
    count > 0
      ? `Notifications, ${count} unread`
      : "Notifications";

  const indicator = visible ? (
    <span
      className={`notification-icon__indicator notification-icon__indicator--${resolvedType}`}
      style={indicatorStyle}
      aria-hidden={resolvedType !== "count"}
    >
      {resolvedType === "count" ? displayCount : null}
    </span>
  ) : null;

  const iconContent = (
    <>
      <span className="notification-icon__icon">{icon}</span>
      {indicator}
    </>
  );

  const rootClassName = [
    "notification-icon",
    `notification-icon--${size}`,
    onClick ? "notification-icon--interactive" : "",
    disabled ? "notification-icon--disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (onClick) {
    return (
      <button
        type="button"
        className={rootClassName}
        style={rootStyle}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel ?? defaultAriaLabel}
        aria-disabled={disabled}
      >
        {iconContent}
      </button>
    );
  }

  return (
    <span
      className={rootClassName}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? defaultAriaLabel}
    >
      {iconContent}
    </span>
  );
};

export default NotificationIcon;
